import React from 'react';
import { HiOutlineSparkles } from "react-icons/hi2";
import { LuRectangleVertical, LuRectangleHorizontal } from "react-icons/lu";
import { BiRectangle } from "react-icons/bi";
import { HiChevronUpDown } from "react-icons/hi2";
import { Menu } from '@headlessui/react';
import ImageStyleSelector from './ImageStyleSelector';

const ImageTaskbar = ({ inputValue, setInputValue, selectedStyle, setSelectedStyle, handleCreate, isLoading }) => {
  const [aspectRatio, setAspectRatio] = React.useState("9:16");

  const aspectRatios = [
    { value: "1:1", icon: BiRectangle, label: "1:1" },
    { value: "16:9", icon: LuRectangleHorizontal, label: "16:9" },
    { value: "9:16", icon: LuRectangleVertical, label: "9:16" },
  ];

  const selectedAspectRatio = aspectRatios.find(ratio => ratio.value === aspectRatio);

  return (
    <div className="fixed bottom-6 left-[240px] right-6 z-50">
      <div className="mx-auto bg-white/90 backdrop-blur-lg border border-gray-200/50 rounded-2xl shadow-lg p-3" style={{ maxWidth: '800px' }}>
        <div className="flex flex-col gap-3">
          {/* Top row with input and generate button */}
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isLoading && inputValue.trim()) {
                  e.preventDefault();
                  handleCreate(aspectRatio);
                }
              }}
              className="flex-1 h-10 px-4 text-black text-sm focus:outline-none border border-gray-200/50 rounded-full bg-transparent min-w-0"
              placeholder="A majestic dragon soaring over ancient ruins at sunset"
            />
            <button
              onClick={() => handleCreate(aspectRatio)}
              className="bg-black text-white font-medium text-sm py-2.5 px-6 rounded-full shadow-sm hover:bg-gray-900 transition-all duration-300 flex items-center justify-center min-w-[120px] whitespace-nowrap"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="grid min-h-[12px] w-full place-items-center">
                  <svg
                    className="text-gray-300 animate-spin"
                    viewBox="0 0 64 64"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                  >
                    <path
                      d="M32 3C35.8083 3 39.5794 3.75011 43.0978 5.20749C46.6163 6.66488 49.8132 8.80101 52.5061 11.4939C55.199 14.1868 57.3351 17.3837 58.7925 20.9022C60.2499 24.4206 61 28.1917 61 32C61 35.8083 60.2499 39.5794 58.7925 43.0978C57.3351 46.6163 55.199 49.8132 52.5061 52.5061C49.8132 55.199 46.6163 57.3351 43.0978 58.7925C39.5794 60.2499 35.8083 61 32 61C28.1917 61 24.4206 60.2499 20.9022 58.7925C17.3837 57.3351 14.1868 55.199 11.4939 52.5061C8.801 49.8132 6.66487 46.6163 5.20749 43.0978C3.7501 39.5794 3 35.8083 3 32C3 28.1917 3.75011 24.4206 5.2075 20.9022C6.66489 17.3837 8.80101 14.1868 11.4939 11.4939C14.1868 8.80099 17.3838 6.66487 20.9022 5.20749C24.4206 3.7501 28.1917 3 32 3L32 3Z"
                      stroke="currentColor"
                      strokeWidth="5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></path>
                    <path
                      d="M32 3C36.5778 3 41.0906 4.08374 45.1692 6.16256C49.2477 8.24138 52.7762 11.2562 55.466 14.9605C58.1558 18.6647 59.9304 22.9531 60.6448 27.4748C61.3591 31.9965 60.9928 36.6232 59.5759 40.9762"
                      stroke="currentColor"
                      strokeWidth="5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-gray-900"
                    ></path>
                  </svg>
                </div>
              ) : (
                <>
                  Generate
                  <HiOutlineSparkles className="h-3.5 w-3.5 ml-1.5 text-white" />
                </>
              )}
            </button>
          </div>

          {/* Bottom row with style and aspect ratio */}
          <div className="flex items-center gap-3 px-2">
            <ImageStyleSelector
              selectedStyle={selectedStyle}
              setSelectedStyle={setSelectedStyle}
            />
            <div className="h-6 w-[1px] bg-gray-200"></div>
            <Menu as="div" className="relative">
              <Menu.Button className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                {selectedAspectRatio && (
                  <>
                    <selectedAspectRatio.icon className="w-4 h-4" />
                    <span>{selectedAspectRatio.value}</span>
                  </>
                )}
                <HiChevronUpDown className="w-4 h-4 text-gray-500" />
              </Menu.Button>
              <Menu.Items className="absolute bottom-full mb-1 w-36 bg-white rounded-lg shadow-lg border border-gray-200/50 py-1 focus:outline-none">
                {aspectRatios.map((ratio) => (
                  <Menu.Item key={ratio.value}>
                    {({ active }) => (
                      <button
                        onClick={() => setAspectRatio(ratio.value)}
                        className={`${
                          active ? 'bg-gray-50' : ''
                        } ${
                          aspectRatio === ratio.value ? 'text-black' : 'text-gray-700'
                        } group flex w-full items-center gap-2 px-3 py-1.5 text-sm`}
                      >
                        <ratio.icon className="w-4 h-4" />
                        <span>{ratio.label}</span>
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Menu>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageTaskbar; 